import type { IconProps } from '../icons/Icon';
import type { StrictPaddingStyle } from '../util/types';

import { getFontSizeStyle } from '../Text/TextLevel';
import { useTheme } from '../ThemeProvider';
import { VisuallyHidden } from '../VisuallyHidden';

import React, { Fragment } from 'react';
import { StyleSheet, View, Pressable, Text, type TextStyle, Platform } from 'react-native';

export type TabSize = 'sm' | 'md';

export interface TabConfig<Id extends string = string> {
    /**
     * Unique identifier for the tab
     */
    id: Id;

    /**
     * Label text to display in the tab
     */
    label: string;

    /**
     * Icon component to display when the tab is not selected
     */
    icon?: React.ReactNode;

    /**
     * Icon component to display when the tab is selected
     * If not provided, the regular icon will be used with a different color
     */
    selectedIcon?: React.ReactNode;

    /**
     * Allows getting a ref to the tab pressable element.
     * Useful for programmatic focus management for accessibility.
     */
    ref?: React.Ref<View>;

    /**
     * Test ID for the tab element
     */
    testID?: string;
}

export interface TabListAccessibilityLabelProps {
    /**
     * Accessible name/short description of tab list
     * @default "tab list"
     */
    description?: string;

    /**
     * The word to use between position and total (e.g., "x of y")
     * - Use ${pos} to denote the current position
     * - Use ${len} to denote the length of the tab list
     * Examples: Arabic's 4 من 8 would be "${pos} ${len} من"
     * @default "${pos} of ${len}"
     */
    positionText?: string;
}

export interface TabMenuProps<Id extends string = string> {
    /**
     * Array of tab configurations
     */
    tabs: TabConfig<Id>[];

    /**
     * Makes Tabs full-width
     */
    full?: boolean;

    /**
     * Size of the tabs. Applies only to tabs with no icon.
     */
    size?: TabSize;

    /**
     * ID of the currently selected tab
     */
    selectedTabId: string;

    /**
     * Callback function when a tab is pressed
     */
    onTabPress: (tabId: Id) => void;

    /**
     * Defines the padding style overrides.
     */
    sx?: StrictPaddingStyle;

    /**
     * Accessible labels to describe parts of the tab list
     */
    a11yLabels?: TabListAccessibilityLabelProps;
}

export function TabMenu<Id extends string = string>({
    tabs,
    full = true,
    size = 'md',
    selectedTabId,
    onTabPress,
    sx,
    a11yLabels,
}: TabMenuProps<Id>): React.JSX.Element {
    const hasIcons = tabs.some((tab) => tab.icon !== undefined);
    const tabHeight = hasIcons ? 64 : size === 'sm' ? 40 : 56;
    const totalItems = tabs.length;
    const { description = 'tab list', positionText = '${pos} of ${len}' } = a11yLabels ?? {};
    const theme = useTheme();
    const isWeb = Platform.OS === 'web';
    const noIconFontSize = getFontSizeStyle(size === 'sm' ? 2 : 3, theme);
    const iconFontSize = getFontSizeStyle(1, theme);

    const styles = StyleSheet.create({
        container: {
            flexDirection: 'row',
            gap: theme.spacing.s3,
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.borderWeak,
            backgroundColor: theme.colors.background,
        },
        tabSelected: {
            borderBottomColor: theme.colors.foregroundActionStrong,
        },
        textSelected: {
            fontWeight: String(theme.fontWeights.bold) as TextStyle['fontWeight'],
            minWidth: 96,
        },
        tab: {
            backgroundColor: theme.colors.background,
            borderBottomColor: 'transparent',
            borderBottomWidth: 2,
            alignItems: 'center',
            paddingVertical: theme.spacing.s3,
            paddingHorizontal: theme.spacing.s2,
        },
        tabFull: {
            flex: 1,
        },
        iconContainer: {
            marginBottom: theme.spacing.s1,
        },
        tabColors: {
            textAlign: 'center',
            color: theme.colors.foregroundActionStrong,
        },
        tabNoIcon: {
            justifyContent: 'center',
            minWidth: 96,
        },
        tabWithIcon: {
            maxWidth: 84,
        },
        textNoIcon: {
            flexShrink: 0,
            alignSelf: 'stretch',
            minWidth: 96,
        },
        // Stacks a hidden bold ghost and the visible label so the tab's width
        // is always the bold width, preventing layout shift when selection toggles.
        textSlot: {
            alignItems: 'center',
            alignSelf: 'stretch',
            minWidth: 96,
        },
        textGhost: {
            opacity: 0,
        },
        textOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
        },
    });
    return (
        <View
            accessibilityRole="tablist"
            style={[
                styles.container,
                sx,
                { minHeight: tabHeight, justifyContent: full ? 'center' : 'flex-start' },
            ]}
        >
            <VisuallyHidden>{description}</VisuallyHidden>
            {tabs.map((tab, index) => {
                const isSelected = tab.id === selectedTabId;
                const hasIcon = tab.icon !== undefined;
                const position = index + 1;
                const positionDescription = positionText
                    .replaceAll('${pos}', position.toString())
                    .replaceAll('${len}', totalItems.toString());

                return (
                    <Fragment key={tab.id}>
                        <Pressable
                            ref={tab.ref}
                            accessibilityRole="tab"
                            accessibilityState={{ selected: isSelected }}
                            {...Platform.select({
                                web: {
                                    'aria-selected': isSelected,
                                }
                            })}
                            accessibilityLabel={tab.label}
                            testID={tab.testID}
                            onPress={() => onTabPress(tab.id)}
                            style={[
                                styles.tab,
                                full && styles.tabFull,
                                isSelected && styles.tabSelected,
                                !hasIcon ? styles.tabNoIcon : styles.tabWithIcon,
                            ]}
                        >
                            {hasIcon && (
                                <View style={styles.iconContainer}>
                                    {React.cloneElement(
                                        (isSelected && tab.selectedIcon
                                            ? tab.selectedIcon
                                            : tab.icon) as React.ReactElement<IconProps>,
                                        {
                                            color: theme.colors.foregroundActionStrong,
                                            'aria-hidden': true,
                                        },
                                    )}
                                </View>
                            )}
                            {!hasIcon && isWeb ? (
                                <View style={styles.textSlot}>
                                    <Text
                                        aria-hidden
                                        numberOfLines={1}
                                        style={[
                                            styles.tabColors,
                                            noIconFontSize,
                                            styles.textSelected,
                                            styles.textGhost,
                                            styles.textNoIcon,
                                        ]}
                                    >
                                        {tab.label}
                                    </Text>
                                    <Text
                                        numberOfLines={1}
                                        style={[
                                            styles.tabColors,
                                            noIconFontSize,
                                            isSelected && styles.textSelected,
                                            styles.textOverlay,
                                        ]}
                                    >
                                        {tab.label}
                                    </Text>
                                </View>
                            ) : (
                                <Text
                                    adjustsFontSizeToFit={hasIcon}
                                    numberOfLines={1}
                                    style={[
                                        styles.tabColors,
                                        hasIcon ? iconFontSize : noIconFontSize,
                                        isSelected && styles.textSelected,
                                        !hasIcon && styles.textNoIcon,
                                    ]}
                                >
                                    {tab.label}
                                </Text>
                            )}
                        </Pressable>
                        <VisuallyHidden>{positionDescription}</VisuallyHidden>
                    </Fragment>
                );
            })}
        </View>
    );
}
